<template>
  <Modal
    class="c-edit-tracked-publishers"
    :class="{ 'c-edit-tracked-publishers--is-mobile': $isMobile }"
    content-class="flex-1 overflow-auto"
    :loading="saving"
    :title="$t('modals.edit_tracked_publishers_modal.title')"
    v-bind="modalAttrs"
  >
    <div class="q-pa-md">
      <i18n-t
        keypath="modals.edit_tracked_publishers_modal.description"
        scope="global"
      >
        <template #frontPages>
          <strong>{{
            $t("modals.edit_tracked_publishers_modal.front_pages")
          }}</strong>
        </template>
        <template #trending>
          <strong>{{
            $t("modals.edit_tracked_publishers_modal.trending")
          }}</strong>
        </template>
      </i18n-t>

      <div class="row col-1">
        <InputSelect
          v-model="selectedRegion"
          class="q-mt-sm"
          clearable
          emit-value
          new-design
          option-label="name"
          option-prop="value"
          :options="filteredRegions"
          :placeholder="$t('modals.edit_tracked_publishers_modal.region')"
        />
      </div>
    </div>
    <BaseSeparator />
    <template
      v-for="(groupedSource, groupIndex) in groupedSources"
      :key="groupIndex"
    >
      <BaseSeparator
        v-if="groupIndex"
        :key="`separator_${groupIndex}`"
      />
      <div class="row q-py-md">
        <div class="c-edit-tracked-publishers__group-logo">
          <img
            :alt="groupedSource.group.label"
            :src="groupedSource.group.logo"
          />
        </div>
        <div class="c-edit-tracked-publishers__sources">
          <div
            v-for="(source, index) in groupedSource.sources"
            :key="index"
          >
            <Checkbox
              v-model="source.tracked"
              new-design
              @update:model-value="save(source, $event)"
            >
              <div class="column q-col-gutter-xs q-px-md">
                <div>{{ source.name }}</div>
                <div
                  v-if="source.section"
                  class="softer"
                >
                  {{ source.section }}
                </div>
              </div>
            </Checkbox>
          </div>
        </div>
      </div>
    </template>

    <template #buttons>
      <BaseButton
        color="primary"
        :label="$t('global.ok')"
        new-design
        @click="done"
      />
    </template>
  </Modal>
</template>

<script>
import { storeToRefs } from "pinia";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseSeparator, InputSelect } from "shared/components/base";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import Modal from "shared/components/core/modals/Modal.vue";
import { regions } from "shared/constants";
import { useGlobalStore } from "shared/stores/global";

export default {
  name: "EditTrackedPublishersModal",
  components: {
    BaseButton,
    BaseSeparator,
    Checkbox,
    InputSelect,
    Modal,
  },
  props: {
    groups: {
      type: Array,
      default: () => [],
    },
    articleRankingSources: {
      type: Array,
      required: true,
    },
  },
  emits: ["edited"],
  setup() {
    const { appConfig } = storeToRefs(useGlobalStore());

    return { appConfig };
  },
  data() {
    return {
      saving: false,
      selectedRegion: null,
    };
  },
  computed: {
    platformRegions() {
      const availableRegions = regions.filter(({ value }) =>
        this.appConfig.regions.includes(value)
      );

      return [
        {
          name: getLocaleText(
            "modals.edit_tracked_publishers_modal.regions.international"
          ),
          value: "international",
        },
        ...availableRegions.map((region) => ({
          ...region,
          name: getLocaleText(
            `modals.edit_tracked_publishers_modal.regions.${region.value}`
          ),
        })),
      ];
    },
    filteredRegions() {
      return this.platformRegions.filter(({ value }) =>
        this.articleRankingSources.some(({ region }) => region === value)
      );
    },
    filteredSources() {
      if (!this.selectedRegion) {
        return this.articleRankingSources;
      }

      return this.articleRankingSources.filter(
        (source) => source.region === this.selectedRegion
      );
    },
    groupedSources() {
      const groupedSources = [];

      this.groups.forEach((group) => {
        const sources = this.filteredSources.filter(
          (item) => item.group === group
        );

        if (!sources.length) {
          return;
        }

        groupedSources.push({
          group: {
            label: group,
            logo: sources[0].logo_url,
          },
          sources,
        });
      });

      return groupedSources;
    },
    modalAttrs() {
      if (this.$isMobile) {
        return {
          fullHeight: true,
        };
      }

      return {
        modalStyle: {
          maxWidth: "660px",
          height: "600px",
        },
        offsetMenu: true,
      };
    },
  },
  methods: {
    async save(source, tracked) {
      try {
        this.saving = true;

        if (tracked) {
          this.$track("Source Tracked", {
            articleRankingSources: source.id,
            context: "frontpages",
          });

          await this.$streemApiV1.post(
            `article_ranking_sources/${source.id}/watch`,
            {
              params: {
                scope: "frontpages",
              },
            }
          );
        } else {
          this.$track("Source Untracked", {
            articleRankingSources: source.id,
            context: "frontpages",
          });

          await this.$streemApiV1.post(
            `article_ranking_sources/${source.id}/unwatch`,
            {
              params: {
                scope: "frontpages",
              },
            }
          );
        }
      } finally {
        this.saving = false;
      }

      this.$emit("edited");
    },
    done() {
      this.$modals.close("EditTrackedPublishersModal");
    },
  },
};
</script>

<style lang="scss" scoped>
.c-edit-tracked-publishers {
  --c-edit-tracked-publishers-columns: 1fr 1fr;

  &__group-logo {
    padding-inline: var(--s-spacing-xl);
    flex-shrink: 0;

    img {
      width: 60px;
    }
  }

  &__sources {
    display: grid;
    grid-template-columns: var(--c-edit-tracked-publishers-columns);
    align-items: center;
    gap: var(--s-spacing-lg);
    flex: 1;
  }

  &--is-mobile {
    --c-edit-tracked-publishers-columns: 1fr;
  }
}

:deep(.checkbox) {
  margin: 0;
}
</style>
